import omitBy from 'lodash/omitBy';
import { useMemo } from 'react';
import { returnData } from 'services/api-client/client';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import type { CreateProjectPayload, ProjectWithoutChildren, SlimProject, UpdateProjectPayload } from 'types/api.types';
import last from 'lodash/last';
import zipObject from 'lodash/zipObject';
import { Policy } from 'cockatiel';
import mapValues from 'lodash/mapValues';
import values from 'lodash/values';
import keyBy from 'lodash/keyBy';
import type { RBACPermissionsAssignments } from 'stores/rq/user-permissions';
import { emptyPermissions, useGetUserPermissions } from 'stores/rq/user-permissions';
import { canCreateSubProject, isProjectReadable } from 'hooks/use-has-permission';
import isError from 'lodash/isError';
import { useCurrentOrganizationId } from 'hooks/use-current-organization-id';
import useApiClient from 'hooks/use-api-client';
import { populateProjectsChildren } from 'utils/populate-projects-children.utils';
import { getQueryClientInstance } from 'stores/rq/common/query-client-provider';
import type { QueryClient, QueryKey } from '@tanstack/react-query';

// Not id-shaped, so it cannot collide with a project whose id happens to be this string.
export const ROOT_CHILDREN_KEY = '__root__';

export const useProjectsCacheKeys = () => {
  const orgId = useCurrentOrganizationId();

  const baseKey = [orgId, 'projects'] as const;
  const cacheKeys = {
    all: [...baseKey, 'all'],
    tree: [...baseKey, 'tree'],
    modulesTesting: [...baseKey, 'modulesTesting'],
    tags: [...baseKey, 'tags'],
    single: (projectId: string) => [...baseKey, 'single', projectId] as const,
    childrenPrefix: [...baseKey, 'children'],
    children: (parentProjectId?: string) => [...baseKey, 'children', parentProjectId ?? ROOT_CHILDREN_KEY] as const,
    searchPrefix: [...baseKey, 'search'],
    search: (term: string) => [...baseKey, 'search', term] as const
  } as const;

  return cacheKeys;
};

type ProjectChildrenQueryKey = readonly [string | undefined, 'projects', 'children', string];

const SEARCH_PAGE_SIZE = 50;

// Module-level, not an inline `[]`: a fresh array each render would miss curateProjectsCached every render.
const EMPTY_PROJECTS_TREE: SlimProject[] = [];

// Safe because every mutation invalidates these prefixes explicitly, and invalidation ignores staleTime.
const PROJECTS_STALE_TIME_MS = 1000 * 60 * 2;

// The global default is Infinity, so without a bound every expanded parent and every search term would keep
// its result for the whole session. gcTime only starts counting once a query has no observers, so nothing
// currently rendered is affected, and re-expanding or coming back to the page stays instant.
const LAZY_PROJECTS_GC_TIME_MS = 1000 * 60 * 10;

// By prefix, not per-key: a project's hasSubProjects is returned as part of its *parent's* children.
export const invalidateChildrenAndSearch = (
  queryClient: QueryClient,
  childrenPrefix: QueryKey,
  searchPrefix: QueryKey
) => {
  queryClient.invalidateQueries({ queryKey: childrenPrefix });
  queryClient.invalidateQueries({ queryKey: searchPrefix });
};

export interface UseGetProjectsOptions {
  enabled?: boolean;
}

export const useGetModulesTestingProject = () => {
  const apiClient = useApiClient();
  const { modulesTesting } = useProjectsCacheKeys();

  return useQuery({
    queryKey: modulesTesting,
    queryFn: ({ queryKey: [orgId] }) => apiClient.projects.getModulesTestingProject(orgId)
  });
};

export const useGetProjectTags = () => {
  const apiClient = useApiClient();
  const { tags } = useProjectsCacheKeys();
  const [orgId] = tags;

  return useQuery({
    queryKey: tags,
    queryFn: ({ queryKey: [orgId] }) => apiClient.projects.findTagsByOrganizationId(orgId),
    enabled: Boolean(orgId),
    placeholderData: []
  });
};

export const useGetProject = (projectId: string) => {
  const apiClient = useApiClient();
  const { single } = useProjectsCacheKeys();

  return useQuery({
    queryKey: single(projectId),
    queryFn: () => apiClient.projects.getById(projectId),
    enabled: Boolean(projectId)
  });
};

export const useGetProjects = ({ enabled = true }: UseGetProjectsOptions = {}) => {
  const apiClient = useApiClient();
  const { all } = useProjectsCacheKeys();
  const [orgId] = all;

  return useQuery({
    queryKey: all,
    queryFn: async ({ queryKey: [orgId] }) => {
      if (!orgId) return [];

      const retry = Policy.handleWhen(
        // The first project is created asynchronously after the organization creation, sometimes we need to query for it multiple times until it exists
        (error: Error) => error.message === noProjectsError
      )
        .retry()
        .delay(500)
        .attempts(16);

      return Policy.wrap(retry)
        .execute(async () => {
          const { data: projects } = await apiClient.projects.findByOrganizationId(orgId);
          if (projects.length === 0) {
            throw new Error(noProjectsError);
          }
          return projects;
        })
        .catch(error => {
          if (isError(error) && error.message === noProjectsError) {
            console.warn(`${noProjectsError}, returning empty array`, orgId);
            return [];
          }
          throw error;
        });
    },
    enabled: enabled && Boolean(orgId),
    placeholderData: []
  });
};

export const useGetProjectsTree = ({ enabled = true }: UseGetProjectsOptions = {}) => {
  const apiClient = useApiClient();
  const { tree } = useProjectsCacheKeys();
  const [orgId] = tree;

  return useQuery({
    queryKey: tree,
    queryFn: async ({ queryKey: [orgId] }) => {
      if (!orgId) return [];

      const retry = Policy.handleWhen(
        // The first project is created asynchronously after the organization creation, sometimes we need to query for it multiple times until it exists
        (error: Error) => error.message === noProjectsError
      )
        .retry()
        .delay(500)
        .attempts(16);

      return Policy.wrap(retry)
        .execute(async () => {
          const { data: projects } = await apiClient.projects.findTreeByOrganizationId(orgId);
          if (projects.length === 0) {
            throw new Error(noProjectsError);
          }
          return projects;
        })
        .catch(error => {
          if (isError(error) && error.message === noProjectsError) {
            console.warn(`${noProjectsError}, returning empty array`, orgId);
            return [];
          }
          throw error;
        });
    },
    enabled: enabled && Boolean(orgId),
    placeholderData: EMPTY_PROJECTS_TREE,
    staleTime: PROJECTS_STALE_TIME_MS
  });
};

// Not routed through curateProjects: populateProjectsChildren reports missing parents to Sentry, which is
// the normal state here.
export const useProjectChildren = (parentProjectId?: string, { enabled = true }: UseGetProjectsOptions = {}) => {
  const apiClient = useApiClient();
  const { children } = useProjectsCacheKeys();
  const queryKey = children(parentProjectId);
  const [orgId] = queryKey;

  return useQuery({
    queryKey,
    queryFn: ({ queryKey: [organizationId] }) =>
      apiClient.projects.findChildren({ organizationId: organizationId!, parentProjectId }),
    enabled: enabled && Boolean(orgId),
    staleTime: PROJECTS_STALE_TIME_MS,
    gcTime: LAZY_PROJECTS_GC_TIME_MS
  });
};

export const useExpandedProjectsChildren = (parentProjectIds: string[]) => {
  const apiClient = useApiClient();
  const { children } = useProjectsCacheKeys();
  const orgId = useCurrentOrganizationId();

  return useQueries({
    queries: parentProjectIds.map(parentProjectId => ({
      queryKey: children(parentProjectId),
      queryFn: ({ queryKey: [organizationId] }: { queryKey: ProjectChildrenQueryKey }) =>
        apiClient.projects.findChildren({ organizationId: organizationId!, parentProjectId }),
      enabled: Boolean(orgId),
      staleTime: PROJECTS_STALE_TIME_MS,
      gcTime: LAZY_PROJECTS_GC_TIME_MS
    })),
    combine: results => ({
      childrenByParentId: zipObject(
        parentProjectIds,
        results.map(result => result.data)
      ),
      pendingParentIds: parentProjectIds.filter((_, index) => results[index].isPending),
      failedParentIds: parentProjectIds.filter((_, index) => results[index].isError)
    })
  });
};

export const useProjectsSearch = (term: string) => {
  const apiClient = useApiClient();
  const { search } = useProjectsCacheKeys();
  const queryKey = search(term);
  const [orgId] = queryKey;

  return useInfiniteQuery({
    queryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam, queryKey: [organizationId] }) => {
      const { data, headers } = await apiClient.projects.searchByOrganizationId({
        organizationId: organizationId!,
        search: term,
        limit: SEARCH_PAGE_SIZE,
        nextToken: pageParam
      });
      return { projects: data, hasMore: headers?.['x-has-more-pages'] === 'true' };
    },
    getNextPageParam: lastPage => (lastPage.hasMore ? last(lastPage.projects)?.id : undefined),
    enabled: Boolean(orgId) && Boolean(term),
    placeholderData: keepPreviousData,
    gcTime: LAZY_PROJECTS_GC_TIME_MS
  });
};

export const useCreateProject = (parentProjectId?: string) => {
  const apiClient = useApiClient();
  const queryClient = getQueryClientInstance();
  const { all, tree, tags, childrenPrefix, searchPrefix } = useProjectsCacheKeys();

  const { refetch: refetchUserPermissions } = useGetUserPermissions();

  return useMutation({
    mutationFn: async (payload: CreateProjectPayload) => {
      const response = await apiClient.projects.create({ ...payload, parentProjectId }).then(returnData);
      await refetchUserPermissions();
      return response;
    },
    onSuccess: project => {
      queryClient.setQueryData(all, (projects?: ProjectWithoutChildren[]) =>
        projects ? [...projects, project] : [project]
      );
      queryClient.setQueryData(tree, (projects?: SlimProject[]) => (projects ? [...projects, project] : [project]));
      queryClient.refetchQueries({ queryKey: all });
      queryClient.refetchQueries({ queryKey: tree });
      queryClient.invalidateQueries({ queryKey: tags });
      invalidateChildrenAndSearch(queryClient, childrenPrefix, searchPrefix);
    }
  });
};

export const useUpdateProject = () => {
  const apiClient = useApiClient();
  const queryClient = getQueryClientInstance();
  const { all, tree, single, tags, childrenPrefix, searchPrefix } = useProjectsCacheKeys();

  return useMutation({
    mutationFn: (request: { id: string; data: UpdateProjectPayload }) =>
      apiClient.projects.update(request.id, request.data).then(returnData),
    onSuccess: project => {
      queryClient.setQueryData(all, (projects?: ProjectWithoutChildren[]) =>
        projects?.map(p => (p.id === project.id ? project : p))
      );
      queryClient.setQueryData(tree, (projects?: SlimProject[]) =>
        projects?.map(p => (p.id === project.id ? project : p))
      );
      queryClient.setQueryData(single(project.id), (prev?: ProjectWithoutChildren) =>
        prev ? { ...prev, ...project } : project
      );
      queryClient.refetchQueries({ queryKey: all });
      queryClient.refetchQueries({ queryKey: tree });
      queryClient.invalidateQueries({ queryKey: tags });
      invalidateChildrenAndSearch(queryClient, childrenPrefix, searchPrefix);
    }
  });
};

export const useArchiveProject = () => {
  const apiClient = useApiClient();
  const queryClient = getQueryClientInstance();
  const { all, tree, single, childrenPrefix, searchPrefix } = useProjectsCacheKeys();

  return useMutation({
    mutationFn: (id: string) => apiClient.projects.archive(id).then(returnData),
    onSuccess: (_, id) => {
      queryClient.setQueryData(all, (projects?: ProjectWithoutChildren[]) => {
        return projects?.map(p => ({ ...p, isArchived: p.id === id ? true : p.isArchived }));
      });
      queryClient.setQueryData(tree, (projects?: SlimProject[]) => {
        return projects?.map(p => ({ ...p, isArchived: p.id === id ? true : p.isArchived }));
      });
      queryClient.setQueryData(single(id), (prev?: ProjectWithoutChildren) =>
        prev ? { ...prev, isArchived: true } : prev
      );
      queryClient.refetchQueries({ queryKey: all });
      queryClient.refetchQueries({ queryKey: tree });
      invalidateChildrenAndSearch(queryClient, childrenPrefix, searchPrefix);
    }
  });
};

export const useMoveProject = () => {
  const apiClient = useApiClient();
  const queryClient = getQueryClientInstance();
  const { all, tree, childrenPrefix, searchPrefix } = useProjectsCacheKeys();

  return useMutation({
    mutationFn: (request: { id: string; targetProjectId: string | null }) =>
      apiClient.projects.move(request.id, request.targetProjectId).then(returnData),
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: all });
      queryClient.refetchQueries({ queryKey: tree });
      invalidateChildrenAndSearch(queryClient, childrenPrefix, searchPrefix);
    }
  });
};

export const useCuratedProjects = (options?: UseGetProjectsOptions) => {
  const { data = [], isPlaceholderData, isFetched, error, refetch: refetchProjects } = useGetProjects(options);
  const {
    data: userPermissionsByScope = {},
    isPlaceholderData: isLoadingPermissions,
    refetch: refetchPermissions
  } = useGetUserPermissions();
  const curatedProjects = useMemo(() => curateProjects(data, userPermissionsByScope), [data, userPermissionsByScope]);

  return {
    isFetched,
    isLoading: isPlaceholderData || isLoadingPermissions,
    error,
    refetch: () => [refetchPermissions(), refetchProjects()],
    ...curatedProjects
  };
};

// curateProjects walks the whole org tree, and useMemo is per hook instance - with ~35 mounted consumers
// that cost O(consumers x org size), which at 23k projects blocked the main thread for 69 seconds on one
// expand. Keyed on both query objects because consumers hold placeholder and loaded data simultaneously.
type CuratedProjectsTree = ReturnType<typeof curateProjects>;
const curatedTreeCache = new WeakMap<object, WeakMap<object, CuratedProjectsTree>>();

const curateProjectsCached = (
  data: SlimProject[],
  userPermissionsByScope: RBACPermissionsAssignments
): CuratedProjectsTree => {
  let byPermissions = curatedTreeCache.get(data);
  if (!byPermissions) {
    byPermissions = new WeakMap();
    curatedTreeCache.set(data, byPermissions);
  }

  let curated = byPermissions.get(userPermissionsByScope);
  if (!curated) {
    curated = curateProjects(data, userPermissionsByScope);
    byPermissions.set(userPermissionsByScope, curated);
  }

  return curated;
};

export const useCuratedProjectsTree = (options?: UseGetProjectsOptions) => {
  const {
    data = EMPTY_PROJECTS_TREE,
    isPlaceholderData,
    isFetched,
    error,
    refetch: refetchProjects
  } = useGetProjectsTree(options);
  const {
    data: userPermissionsByScope = emptyPermissions,
    isPlaceholderData: isLoadingPermissions,
    refetch: refetchPermissions
  } = useGetUserPermissions();
  const curatedProjects = curateProjectsCached(data, userPermissionsByScope);

  return {
    isFetched,
    isLoading: isPlaceholderData || isLoadingPermissions,
    error,
    refetch: () => [refetchPermissions(), refetchProjects()],
    ...curatedProjects
  };
};

export const curateProjects = <T extends SlimProject>(
  data: T[],
  userPermissionsByScope: RBACPermissionsAssignments
) => {
  const projectsWithoutChildren = keyBy(data, 'id');
  const projectsWithReadablePermission = initProjectsReadability(projectsWithoutChildren, userPermissionsByScope);
  const projects = populateProjectsChildren(projectsWithReadablePermission);
  const projectsAsArray = values(projects);
  const activeProjects = populateProjectsChildren(omitBy(projectsWithReadablePermission, 'isArchived'));
  const activeRootProjects = omitBy(activeProjects, 'parentProjectId');

  return {
    projects,
    projectsAsArray,
    activeProjects,
    activeRootProjects
  };
};

const noProjectsError = 'No projects in the organization';

const initProjectsReadability = <T extends SlimProject>(
  projects: Record<string, T>,
  userPermissionsByScope: RBACPermissionsAssignments
) => {
  return mapValues(projects, project => ({
    ...project,
    isReadable: userPermissionsByScope ? isProjectReadable(project, userPermissionsByScope) : true,
    canCreateSubProject: userPermissionsByScope ? canCreateSubProject(project, userPermissionsByScope) : true,
    children: []
  }));
};
